'use client';
export { Item, Section } from '@react-stately/collections';
import { PressResponder } from '@react-aria/interactions';
import { useLocalizedStringFormatter } from '@react-aria/i18n';
import { useSelect, HiddenSelect } from '@react-aria/select';
import { useResizeObserver, useLayoutEffect } from '@react-aria/utils';
import { useSelectState } from '@react-stately/select';
import { forwardRef, useRef, useState, useCallback } from 'react';
import { FieldButton } from '@keystar/ui/button';
import { useProviderProps, useProvider } from '@keystar/ui/core';
import { FieldPrimitive } from '@keystar/ui/field';
import { chevronsUpDownIcon } from '@keystar/ui/icon/icons/chevronsUpDownIcon';
import { Icon } from '@keystar/ui/icon';
import { useListBoxLayout, ListBoxBase, listStyles } from '@keystar/ui/listbox';
import { Tray, Popover } from '@keystar/ui/overlays';
import { ProgressCircle } from '@keystar/ui/progress';
import { useSlotProps, SlotProvider } from '@keystar/ui/slots';
import { useIsMobileDevice, classNames, css, tokenSchema } from '@keystar/ui/style';
import { Text } from '@keystar/ui/typography';
import { isReactText } from '@keystar/ui/utils';
import { jsx, jsxs } from 'react/jsx-runtime';

const localizedMessages = {
  "ar-AE": {
    "loading": `جارٍ التحميل...`,
    "placeholder": `حدد خيارًا...`
  },
  "bg-BG": {
    "loading": `Зареждане...`,
    "placeholder": `Изберете опция`
  },
  "cs-CZ": {
    "loading": `Načítání...`,
    "placeholder": `Vyberte vhodnou možnost...`
  },
  "da-DK": {
    "loading": `Indlæser ...`,
    "placeholder": `Vælg en mulighed ...`
  },
  "de-DE": {
    "loading": `Laden...`,
    "placeholder": `Option auswählen...`
  },
  "el-GR": {
    "loading": `Φόρτωση...`,
    "placeholder": `Επιλέξτε…`
  },
  "en-US": {
    "placeholder": `Select an option…`,
    "loading": `Loading…`
  },
  "es-ES": {
    "loading": `Cargando…`,
    "placeholder": `Selecciona una de estas opciones...`
  },
  "et-EE": {
    "loading": `Laadimine...`,
    "placeholder": `Valige valikuline...`
  },
  "fi-FI": {
    "loading": `Ladataan…`,
    "placeholder": `Valitse vaihtoehto...`
  },
  "fr-FR": {
    "loading": `Chargement...`,
    "placeholder": `Sélectionnez une option...`
  },
  "he-IL": {
    "loading": `טוען...`,
    "placeholder": `בחר אפשרות...`
  },
  "hr-HR": {
    "loading": `Učitavam...`,
    "placeholder": `Odaberite opciju`
  },
  "hu-HU": {
    "loading": `Betöltés folyamatban…`,
    "placeholder": `Válasszon egy opciót…`
  },
  "it-IT": {
    "loading": `Caricamento...`,
    "placeholder": `Seleziona un’opzione...`
  },
  "ja-JP": {
    "loading": `読み込み中...`,
    "placeholder": `オプションを選択...`
  },
  "ko-KR": {
    "loading": `로드 중`,
    "placeholder": `선택 사항 선택`
  },
  "lt-LT": {
    "loading": `Įkeliama...`,
    "placeholder": `Pasirinkite parinktį...`
  },
  "lv-LV": {
    "loading": `Notiek ielāde...`,
    "placeholder": `Atlasiet opciju...`
  },
  "nb-NO": {
    "loading": `Laster inn ...`,
    "placeholder": `Velg et alternativ...`
  },
  "nl-NL": {
    "loading": `Laden...`,
    "placeholder": `Optie selecteren...`
  },
  "pl-PL": {
    "loading": `Ładowanie...`,
    "placeholder": `Wybierz opcję...`
  },
  "pt-BR": {
    "loading": `Carregando...`,
    "placeholder": `Selecione uma opção...`
  },
  "pt-PT": {
    "loading": `A carregar...`,
    "placeholder": `Selecionar uma opção...`
  },
  "ro-RO": {
    "loading": `Se încarcă...`,
    "placeholder": `Selectați o opțiune`
  },
  "ru-RU": {
    "loading": `Загрузка...`,
    "placeholder": `Выбрать параметр...`
  },
  "sk-SK": {
    "loading": `Načítava sa...`,
    "placeholder": `Vyberte možnosť...`
  },
  "sl-SI": {
    "loading": `Nalaganje...`,
    "placeholder": `Izberite možnost`
  },
  "sr-SP": {
    "loading": `Učitavam...`,
    "placeholder": `Izaberite opciju`
  },
  "sv-SE": {
    "loading": `Läser in...`,
    "placeholder": `Välj ett alternativ...`
  },
  "tr-TR": {
    "loading": `Yükleniyor...`,
    "placeholder": `Bir seçim yapın…`
  },
  "uk-UA": {
    "loading": `Завантаження…`,
    "placeholder": `Виберіть опцію...`
  },
  "zh-CN": {
    "loading": `正在加载...`,
    "placeholder": `选择一个选项...`
  },
  "zh-T": {
    "loading": `正在載入`,
    "placeholder": `選取一個選項`
  }
};

function Picker(props, forwardedRef) {
  props = useSlotProps(props, 'picker');
  props = useProviderProps(props);
  let stringFormatter = useLocalizedStringFormatter(localizedMessages);
  let {
    align = 'start',
    autoComplete,
    autoFocus,
    direction = 'bottom',
    isDisabled,
    label,
    menuWidth,
    name,
    placeholder = stringFormatter.format('placeholder'),
    prominence,
    shouldFlip = true
  } = props;
  let popoverRef = useRef(null);
  let triggerRef = useRef(null);
  let listboxRef = useRef(null);

  // We create the listbox layout in Picker and pass it to ListBoxBase below
  // so that the layout information can be cached even while the listbox is not mounted.
  let layout = useListBoxLayout();
  let state = useSelectState(props);
  let {
    labelProps,
    triggerProps,
    valueProps,
    menuProps,
    descriptionProps,
    errorMessageProps
  } = useSelect(props, state, triggerRef);
  let isMobile = useIsMobileDevice();
  let isLoadingInitial = props.isLoading && state.collection.size === 0;
  let isLoadingMore = props.isLoading && state.collection.size > 0;

  // On small screen devices, the listbox is rendered in a tray, otherwise a popover.
  let listbox = /*#__PURE__*/jsx(ListBoxBase, {
    ...menuProps,
    ref: listboxRef,
    disallowEmptySelection: true,
    autoFocus: state.focusStrategy || true,
    shouldSelectOnPressUp: true,
    focusOnPointerEnter: true,
    layout: layout,
    state: state,
    UNSAFE_className: listStyles,
    isLoading: isLoadingMore,
    onLoadMore: props.onLoadMore
  });

  // Measure the width of the button to inform the width of the menu (below).
  let [buttonWidth, setButtonWidth] = useState();
  let {
    scale
  } = useProvider();
  let onResize = useCallback(() => {
    if (!isMobile && triggerRef.current) {
      let width = triggerRef.current.offsetWidth;
      setButtonWidth(width);
    }
  }, [triggerRef, setButtonWidth, isMobile]);
  useResizeObserver({
    ref: triggerRef,
    onResize: onResize
  });
  useLayoutEffect(onResize, [scale, state.selectedKey, onResize]);
  let overlay;
  if (isMobile) {
    overlay = /*#__PURE__*/jsx(Tray, {
      state: state,
      children: listbox
    });
  } else {
    // Match the width of the button, unless explicitly overridden by the
    // consumer via `menuWidth` prop. The width should never be less than the
    // invoking button.
    let style = {
      minWidth: buttonWidth,
      width: menuWidth !== null && menuWidth !== void 0 ? menuWidth : buttonWidth
    };

    // FIXME: should close on blur
    // @see http://localhost:6006/?path=/story/pickers-picker--focus
    // open then tab to next element
    overlay = /*#__PURE__*/jsx(Popover, {
      UNSAFE_style: style,
      ref: popoverRef,
      placement: `${direction} ${align}`,
      shouldFlip: shouldFlip,
      hideArrow: true,
      state: state,
      triggerRef: triggerRef,
      scrollRef: listboxRef,
      children: listbox
    });
  }
  let contents = state.selectedItem ? state.selectedItem.rendered : placeholder;
  if (isReactText(contents)) {
    contents = /*#__PURE__*/jsx(Text, {
      children: contents
    });
  }
  let picker = /*#__PURE__*/jsxs("div", {
    children: [/*#__PURE__*/jsx(HiddenSelect, {
      autoComplete: autoComplete,
      isDisabled: isDisabled,
      label: label,
      name: name,
      state: state,
      triggerRef: triggerRef
    }), /*#__PURE__*/jsx(PressResponder, {
      ...triggerProps,
      children: /*#__PURE__*/jsxs(FieldButton, {
        "aria-required": true,
        prominence: prominence,
        ref: triggerRef,
        isActive: state.isOpen,
        isDisabled: isDisabled,
        autoFocus: autoFocus,
        UNSAFE_className: classNames(css({
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          position: 'relative',
          width: '100%',
          /* Ensure that changing the selected item doesn't affect the size of the dropdown and its parents */
          contain: 'size'
        })),
        children: [/*#__PURE__*/jsx(SlotProvider, {
          slots: {
            icon: {
              marginEnd: 'small'
            },
            text: {
              ...valueProps,
              // when no item is selected, we're styling the placeholder
              color: !state.selectedItem ? 'neutralSecondary' : 'inherit'
              // weight: state.selectedItem ? 'medium' : undefined,
            },
            // we try to maintain most of the selected item's rendered content
            // within the button, but description text is too long
            description: {
              isHidden: true
            }
          },
          children: contents
        }), isLoadingInitial && /*#__PURE__*/jsx(ProgressCircle, {
          isIndeterminate: true,
          size: "small",
          "aria-label": stringFormatter.format('loading'),
          UNSAFE_className: css({
            marginInlineStart: tokenSchema.size.space.small
          })
        }), /*#__PURE__*/jsx(Icon, {
          src: chevronsUpDownIcon,
          UNSAFE_className: css({
            marginInlineStart: tokenSchema.size.space.small
          })
        })]
      })
    }), state.collection.size === 0 ? null : overlay]
  });
  return /*#__PURE__*/jsx(FieldPrimitive, {
    width: "alias.singleLineWidth",
    ...props,
    ref: forwardedRef,
    labelProps: labelProps,
    descriptionProps: descriptionProps,
    errorMessageProps: errorMessageProps,
    supplementRequiredState: true,
    children: picker
  });
}

// forwardRef doesn't support generic parameters, so cast the result to the correct type
// https://stackoverflow.com/questions/58469229/react-with-typescript-generics-while-using-react-forwardref

/**
 * Pickers allow users to choose a single option from a collapsible list of options when space is limited.
 */
const _Picker = /*#__PURE__*/forwardRef(Picker);

export { _Picker as Picker };
